import { useEffect, useState, useRef } from "preact/hooks";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFlavorLineCollection } from "react-hooks/useFlavorLineCollection";
import ProductQty from "react-components/ProductQty"
import { useScreenSize, SCREEN_SIZES } from 'react-hooks/useScreenSize';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

// Helper to get option values for a variant group, filtering by launch_timestamp
function getOptionValues(variants, idx, nowTimestamp) {
    return Array.from(
        new Set(
            variants
                .filter(v => !v.launch_timestamp || v.launch_timestamp <= nowTimestamp)
                .map(v => v.options?.[idx])
                .filter(Boolean)
        )
    );
}

// Helper to get URL parameter by name (ported from legacy)
function getParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '[$&]');
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

// Helper to build bundle image URL based on selected bundle options (handles inconsistent naming)
function buildBundleImageUrlFromSelections({ bundleSelections, bundleComponents, product }) {
    // Find selected lid and bottle
    let lid = null, bottle = null, lidColor = null, bottleColor = null, bottleSize = null, bottleType = null;

    bundleComponents?.forEach((comp, idx) => {
        const selectedId = bundleSelections[idx];
        if (!selectedId) return;
        if (comp.title && comp.title.toLowerCase().includes('lid')) {
            lid = comp.variants.find(v => String(v.id) === String(selectedId));
            lidColor = (lid?.option1 || lid?.title || "").toUpperCase().replace(/[^A-Z0-9]/g, '');
        }

        if (comp.title && comp.title.toLowerCase().includes('bottle')) {
            bottle = comp.variants.find(v => String(v.id) === String(selectedId));
            bottleColor = (bottle?.option1 || bottle?.title || "").toUpperCase().replace(/[^A-Z0-9]/g, '');
            
            // Try to infer size from comp.title or variant title
            let size = "";
            // 1. Try to get from product.title (e.g. "12oz. Something")
            if (product && typeof product.title === "string") {
                const match = product.title.match(/^(\d+)\s?oz\.?/i);
                if (match) {
                    size = match[1];
                }
            }

            // 2. Fallback to comp.title or variant title if not found
            if (!size && comp.title && comp.title.match(/\d+oz/i)) {
                size = comp.title.match(/\d+oz/i)[0].replace(/[^0-9]/g, '');
            } else if (!size && bottle?.title && bottle.title.match(/\d+oz/i)) {
                size = bottle.title.match(/\d+oz/i)[0].replace(/[^0-9]/g, '');
            }
            bottleSize = `${size  }OZ`;

            // --- Update: bottleType from product.title ---
            let t = (product.title || "").toLowerCase();
            if (t.includes('grip stainless')) bottleType = 'GRIPSTAINLESS';
            else if (t.includes('grip squeeze')) bottleType = 'GRIPSQUEEZE';
            else if (t.includes('stainless')) bottleType = 'STAINLESS';
            else if (t.includes('plastic') && t.includes('squeeze')) bottleType = 'INSULATEDSQUEEZE';
            else if (t.includes('plastic')) bottleType = 'PLASTIC';
            else if (t.includes('squeeze')) bottleType = 'SQUEEZE';
            else bottleType = 'PLASTIC';
        }
    });

    // Fallbacks
    if (!lidColor) lidColor = 'BLUE';
    if (!bottleColor) bottleColor = 'BLUE';

    // --- Special logic for GRIPSTAINLESS bottle alt color ---
    let altColor = '';
    if (bottleType === 'GRIPSTAINLESS') {
        if (bottleColor === 'WHITE') altColor = 'BLUE';
        else altColor = 'WHITE';
    }

    // --- Build image name ---
    let imgName = '';
    // original files
    // if (bottleType === 'STAINLESS') {
    //     imgName = `LID_1_${lidColor}_${bottleSize}_METAL_BOTTLE_1_${bottleColor}.png?v=4`;
    // } else if (bottleType === 'PLASTIC') {
    //     imgName = `LID_1_${lidColor}_${bottleSize}_PLASTIC_BOTTLE_1.png?v=4`;
    // } else if (bottleType === 'INSULATEDSQUEEZE') {
    //     imgName = `LID_1_${bottleColor}_${bottleSize}_INSULATED_SQUEEZE_BOTTLE_1.png?v=4`;
    // } else if (bottleType === 'GRIPSQUEEZE') {
    //     imgName = `LID_1_${bottleColor}_${bottleSize}_GRIP_SQUEEZE_BOTTLE_1_${bottleColor}.png?v=4`;
    // } else if (bottleType === 'GRIPSTAINLESS') {
    //     imgName = `LID_1_${bottleColor}_${bottleSize}_GRIP_METAL_BOTTLE_1_${altColor}.png?v=4`;
    // } else {
    //     imgName = `LID_1_${lidColor}_${bottleSize}_PLASTIC_BOTTLE_1.png?v=4`;
    // }

    // 1x1's
    if (bottleType === 'STAINLESS') {
        imgName = `1-${lidColor}-${bottleColor}-METAL-BOTTLE-${bottleSize}.png?v=4`;
    } else if (bottleType === 'PLASTIC') {
        imgName = `LID-${lidColor}-PLASTIC-BOTTLE-${bottleSize}.png?v=4`;
    } else if (bottleType === 'INSULATEDSQUEEZE') {
        imgName = `LID-1-${bottleColor}_${bottleSize}_INSULATED_SQUEEZE_BOTTLE_1.png?v=4`;
    } else if (bottleType === 'GRIPSQUEEZE') {
        imgName = `LID-1-${bottleColor}_${bottleSize}_GRIP_SQUEEZE_BOTTLE_1_${bottleColor}.png?v=4`;
    } else if (bottleType === 'GRIPSTAINLESS') {
        imgName = `LID-1-${bottleColor}_${bottleSize}_GRIP_METAL_BOTTLE_1_${altColor}.png?v=4`;
    } else {
        imgName = `LID-${lidColor}-PLASTIC-BOTTLE-${bottleSize}.png?v=4`;
    }

    return `https://cdn.shopify.com/s/files/1/0050/5217/0330/files/${imgName}`;
}

const ProductModal = ({
    variant,
    onClose,
    product,
    variants,
    openModalById,
    sipBreadcrumbs,
    modalGalleryImages,
    onFlavorSelect,
    activeGalleryIdx,
    bundleCounts = {},
    bundleComponents: bundleComponentsProp = null,
}) => {
    let actualProduct = product;
    
    // Nutrition facts modal
    const nutritionRef = useRef(null);

    // Use allVariants as the source of truth for variants
    const allVariantsRaw = variants || actualProduct?.variants || [];
    const nowTimestamp = typeof window !== "undefined" && window.cirkulServices && window.cirkulServices.nowTimestamp
        ? window.cirkulServices.nowTimestamp
        : Math.floor(Date.now() / 1000);

    // Only show variants that are launched
    const allVariants = allVariantsRaw.edges ? allVariantsRaw.edges
    ?.map(edge => edge.node) : allVariantsRaw
    .filter(v => !v.launch_timestamp || v.launch_timestamp <= nowTimestamp);

    const hasVariants = Array.isArray(allVariants) && allVariants.length > 1;

    // Only use the first variant's id for single-variant products, otherwise use selected variant logic
    let initialVariant;
    if (Array.isArray(allVariants)) {
        // Ensure initially displayed variant is not out of stock
        initialVariant = allVariants.find((variant) => variant.id && variant.available && !variant.isOutOfStock);
    }
    initialVariant = initialVariant || variant || allVariants[0] || {};

    // --- Option group extraction ---
    let optionGroups = [];
    if (actualProduct && Array.isArray(actualProduct.variantGroups) && actualProduct.variantGroups.length > 0) {
        optionGroups = actualProduct.variantGroups.map((group) => ({
            name: group.name,
            values: group.values
        }));
    } else if (hasVariants && Array.isArray(actualProduct?.options) && actualProduct.options.length > 0 && allVariantsRaw[0]?.options) {
        optionGroups = actualProduct.options.map((name, idx) => ({
            name,
            values: getOptionValues(allVariantsRaw, idx, nowTimestamp)
        }));
    } else if (hasVariants) {
        optionGroups = [];
        if (allVariantsRaw.some(v => v.option1)) optionGroups.push({ name: "Option 1", values: Array.from(new Set(allVariantsRaw.filter(v => !v.launch_timestamp || v.launch_timestamp <= nowTimestamp).map(v => v.option1).filter(Boolean))) });
        if (allVariantsRaw.some(v => v.option2)) optionGroups.push({ name: "Option 2", values: Array.from(new Set(allVariantsRaw.filter(v => !v.launch_timestamp || v.launch_timestamp <= nowTimestamp).map(v => v.option2).filter(Boolean))) });
        if (allVariantsRaw.some(v => v.option3)) optionGroups.push({ name: "Option 3", values: Array.from(new Set(allVariantsRaw.filter(v => !v.launch_timestamp || v.launch_timestamp <= nowTimestamp).map(v => v.option3).filter(Boolean))) });
    }

    // --- Nutirion Facts Modal ---
    const [nutritionVisible, setNutritionVisible] = useState(false);

    // --- Bundle support ---
 // Prefer bundleComponents prop if present, else synthesize from bundleCounts
    let bundleComponents = null;
    if (Array.isArray(bundleComponentsProp) && bundleComponentsProp.length > 0) {
        bundleComponents = bundleComponentsProp;
    } else {
        // Synthesize bundleComponents from bundleCounts and product variant arrays
        bundleComponents = [];

        // Lids
        if (bundleCounts.lidsCount > 0 && Array.isArray(actualProduct.lid_variants) && actualProduct.lid_variants.length > 0) {
            for (let i = 0; i < bundleCounts.lidsCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.lidsCount > 1 ? `Lid ${i + 1}` : "Lid",
                    variants: actualProduct.lid_variants
                });
            }
        }

        // Metal Bottles
        if (bundleCounts.metalBottlesCount > 0 && Array.isArray(actualProduct.metal_bottle_variants) && actualProduct.metal_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.metalBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.metalBottlesCount > 1 ? `Bottle ${i + 1}` : "Bottle",
                    variants: actualProduct.metal_bottle_variants
                });
            }
        }
        // Metal 42oz Bottles
        if (bundleCounts.metal42ozBottlesCount > 0 && Array.isArray(actualProduct.metal_42oz_bottle_variants) && actualProduct.metal_42oz_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.metal42ozBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.metal42ozBottlesCount > 1 ? `Bottle ${i + 1}` : "Bottle",
                    variants: actualProduct.metal_42oz_bottle_variants
                });
            }
        }
        // Metal 32oz Bottles
        if (bundleCounts.metal32ozBottlesCount > 0 && Array.isArray(actualProduct.metal_32oz_bottle_variants) && actualProduct.metal_32oz_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.metal32ozBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.metal32ozBottlesCount > 1 ? `Bottle ${i + 1}` : "Bottle",
                    variants: actualProduct.metal_32oz_bottle_variants
                });
            }
        }
        // Metal 12oz Bottles
        if (bundleCounts.metal12ozBottlesCount > 0 && Array.isArray(actualProduct.metal_12oz_bottle_variants) && actualProduct.metal_12oz_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.metal12ozBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.metal12ozBottlesCount > 1 ? `Bottle ${i + 1}` : "Bottle",
                    variants: actualProduct.metal_12oz_bottle_variants
                });
            }
        }
        // Plastic Bottles
        if (bundleCounts.plasticBottlesCount > 0 && Array.isArray(actualProduct.plastic_bottle_variants) && actualProduct.plastic_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.plasticBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.plasticBottlesCount > 1 ? `Bottle ${i + 1}` : "Bottle",
                    variants: actualProduct.plastic_bottle_variants
                });
            }
        }
        // Sleeves
        if (bundleCounts.sleevesCount > 0 && Array.isArray(actualProduct.sleeve_variants) && actualProduct.sleeve_variants.length > 0) {
            for (let i = 0; i < bundleCounts.sleevesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.sleevesCount > 1 ? `Sleeve ${i + 1}` : "Sleeve",
                    variants: actualProduct.sleeve_variants
                });
            }
        }
        // Mini Plastic Bottles
        if (bundleCounts.miniPlasticBottlesCount > 0 && Array.isArray(actualProduct.mini_plastic_bottle_variants) && actualProduct.mini_plastic_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.miniPlasticBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.miniPlasticBottlesCount > 1 ? `Mini Bottle ${i + 1}` : "Mini Bottle",
                    variants: actualProduct.mini_plastic_bottle_variants
                });
            }
        }
        // Plastic 62oz Bottles
        if (bundleCounts.plastic62ozBottlesCount > 0 && Array.isArray(actualProduct.plastic_62oz_bottle_variants) && actualProduct.plastic_62oz_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.plastic62ozBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.plastic62ozBottlesCount > 1 ? `62oz Bottle ${i + 1}` : "62oz Bottle",
                    variants: actualProduct.plastic_62oz_bottle_variants
                });
            }
        }
        // Plastic 42oz Bottles
        if (bundleCounts.plastic42ozBottlesCount > 0 && Array.isArray(actualProduct.plastic_42oz_bottle_variants) && actualProduct.plastic_42oz_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.plastic42ozBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.plastic42ozBottlesCount > 1 ? `42oz Bottle ${i + 1}` : "42oz Bottle",
                    variants: actualProduct.plastic_42oz_bottle_variants
                });
            }
        }
        // Plastic 32oz Bottles
        if (bundleCounts.plastic32ozBottlesCount > 0 && Array.isArray(actualProduct.plastic_32oz_bottle_variants) && actualProduct.plastic_32oz_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.plastic32ozBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.plastic32ozBottlesCount > 1 ? `32oz Bottle ${i + 1}` : "32oz Bottle",
                    variants: actualProduct.plastic_32oz_bottle_variants
                });
            }
        }
        // Squeeze Bottles
        if (bundleCounts.squeezeBottlesCount > 0 && Array.isArray(actualProduct.squeeze_bottle_variants) && actualProduct.squeeze_bottle_variants.length > 0) {
            for (let i = 0; i < bundleCounts.squeezeBottlesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.squeezeBottlesCount > 1 ? `Squeeze Bottle ${i + 1}` : "Squeeze Bottle",
                    variants: actualProduct.squeeze_bottle_variants
                });
            }
        }
        // Mini Sleeves
        if (bundleCounts.miniSleevesCount > 0 && Array.isArray(actualProduct.mini_sleeve_variants) && actualProduct.mini_sleeve_variants.length > 0) {
            for (let i = 0; i < bundleCounts.miniSleevesCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.miniSleevesCount > 1 ? `Mini Sleeve ${i + 1}` : "Mini Sleeve",
                    variants: actualProduct.mini_sleeve_variants
                });
            }
        }
        // 32oz Sleeves
        if (bundleCounts.sleeves32ozCount > 0 && Array.isArray(actualProduct.sleeve_32oz_variants) && actualProduct.sleeve_32oz_variants.length > 0) {
            for (let i = 0; i < bundleCounts.sleeves32ozCount; i++) {
                bundleComponents.push({
                    title: bundleCounts.sleeves32ozCount > 1 ? `32oz Sleeve ${i + 1}` : "32oz Sleeve",
                    variants: actualProduct.sleeve_32oz_variants
                });
            }
        }
    }

    // Check if product is out of stock - now dynamic based on selections
    const [isCurrentSelectionOutOfStock, setIsCurrentSelectionOutOfStock] = useState(false);

    // State for main product variant
    const [selectedVariant, setSelectedVariant] = useState(() => initialVariant);
    // State for bundle component selections
    const [bundleSelections, setBundleSelections] = useState(() => {
        if (!bundleComponents) return {};
        const initial = {};
        bundleComponents.forEach((comp, idx) => {
            initial[idx] = comp.variants?.[0]?.id || null;
        });
        return initial;
    });
    const [quantity, setQuantity] = useState(1);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [localActiveGalleryIdx, setLocalActiveGalleryIdx] = useState(activeGalleryIdx || 0);

    // --- NEW: Track which bundle component is currently controlling the modal image ---
    const [activeBundleImage, setActiveBundleImage] = useState(null);

    // --- NEW: On mount, if this is a bundle, preselect the image to match the default bundle selections ---
    useEffect(() => {
        if (bundleComponents && bundleComponents.length > 0) {
            // Only try to build image if at least a lid and a bottle are selected
            const selectedTitles = bundleComponents?.map((comp, idx) =>
                bundleSelections[idx] && comp.title ? comp.title.toLowerCase() : null
            ) || [];
            const hasLid = selectedTitles.some(t => t && t.includes('lid'));
            const hasBottle = selectedTitles.some(t => t && t.includes('bottle'));
            if (hasLid && hasBottle) {
                const url = buildBundleImageUrlFromSelections({ bundleSelections, bundleComponents, product });
                setActiveBundleImage(url);
            }
        }
        // Only run on mount or if bundleComponents changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [/* only run on mount and bundleComponents change */]);

    useEffect(() => { setImgLoaded(false); }, [selectedVariant, activeBundleImage]);

    // Update out of stock status when bundle selections or main variant changes
    useEffect(() => {
        const checkInventoryStatus = () => {
            // 1. First priority: Check the out_of_stock metafield (admin override)
            if (actualProduct?.isOutOfStock === true) {
                return true;
            }

            // 2. Check selected variant out of stock status
            if (selectedVariant?.isOutOfStock === true) {
                return true;
            }

            // 3. Check selected variant available status
            if (selectedVariant && !selectedVariant.available) {
                return true;
            }

            // 4. Check bundle component selections for out of stock variants
            if (bundleComponents && Array.isArray(bundleComponents) && bundleComponents.length > 0) {
                for (const [idx, comp] of bundleComponents.entries()) {
                    const selectedVariantId = bundleSelections[idx];
                    if (selectedVariantId && comp.variants && Array.isArray(comp.variants)) {
                        const selectedBundleVariant = comp.variants.find(v => String(v.id) === String(selectedVariantId));
                        if (selectedBundleVariant) {
                            // Check bundle variant out of stock metafield
                            if (selectedBundleVariant.isOutOfStock === true) {
                                return true;
                            }
                            
                            // Check bundle variant available status
                            if (!selectedBundleVariant.available) {
                                return true;
                            }
                        }
                    }
                }
            }

            return false;
        };

        try {
            const outOfStock = checkInventoryStatus();
            setIsCurrentSelectionOutOfStock(outOfStock);
        } catch (error) {
            console.error('Error in out of stock check useEffect:', error);
            // Fallback to original static check
            setIsCurrentSelectionOutOfStock(actualProduct?.isOutOfStock === true);
        }
    }, [actualProduct?.isOutOfStock, selectedVariant, bundleSelections, bundleComponents]);

    // Option selection for main product
    const getCurrentOptionValues = () => {
        if (selectedVariant.options && Array.isArray(selectedVariant.options) && selectedVariant.options.length) {
            return selectedVariant.options;
        }
        if (allVariants[0]?.options && Array.isArray(allVariants[0].options) && allVariants[0].options.length) {
            return allVariants[0].options;
        }
        return [
            selectedVariant.option1,
            selectedVariant.option2,
            selectedVariant.option3
        ].slice(0, optionGroups.length);
    };
    const findMatchingVariant = (opts) => {
        if (optionGroups.length && allVariants[0]?.options) {
            return allVariants.find(v =>
                (!v.launch_timestamp || v.launch_timestamp <= nowTimestamp) &&
                v.options &&
                v.options.length === opts.length &&
                opts.every((val, idx) => v.options[idx] === val)
            );
        } 
        return allVariants.find(v =>
            (!v.launch_timestamp || v.launch_timestamp <= nowTimestamp) &&
            optionGroups.every((group, idx) => {
                const optVal = opts[idx];
                if (!optVal) return true;
                return v[`option${idx + 1}`] === optVal;
            })
        );
    };
    const handleOptionChange = (idx, value) => {
        let opts = getCurrentOptionValues().slice();
        opts[idx] = value;
        const match = findMatchingVariant(opts);
        if (match) setSelectedVariant(match);
    };

    // --- NEW: When a bundle dropdown changes, update the modal image if possible ---
    const handleBundleChange = (compIdx, variantId) => {
        setBundleSelections(prev => {
            const next = { ...prev, [compIdx]: variantId };
            // Only try to build image if at least a lid and a bottle are selected
            const selectedTitles = bundleComponents?.map((comp, idx) =>
                next[idx] && comp.title ? comp.title.toLowerCase() : null
            ) || [];
            const hasLid = selectedTitles.some(t => t && t.includes('lid'));
            const hasBottle = selectedTitles.some(t => t && t.includes('bottle'));
            if (hasLid && hasBottle) {
                const url = buildBundleImageUrlFromSelections({ bundleSelections: next, bundleComponents, product });
                setActiveBundleImage(url);
            } else {
                setActiveBundleImage(null);
            }
            return next;
        });
    };

    // Quantity
    const handleQuantityChange = (e) => {
        setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1));
    };

    //Normalize id
    const normalizeShopifyId = (id) => {
        if (typeof id === 'string' && id.includes('gid://')) {
            return id.split('/').pop();
        }
        return String(id); // fallback for numeric ids
    };
    

    // Add to cart (main + bundle)
    const handleAdd = (e) => {
        e.preventDefault();

        let variantIdToUse;

        if (!hasVariants && Array.isArray(allVariants) && allVariants.length === 1 && allVariants[0]?.id) {
            // Single-variant product
            variantIdToUse = allVariants[0].id;
        } else if (hasVariants && selectedVariant && !product.tags.includes('Digital Gift Card')) {
            let found = null;
            // For multi-variant products, match by option1/option2/option3 (Shopify style)
            found = allVariants.find(v =>
                (!v.launch_timestamp || v.launch_timestamp <= nowTimestamp) &&
                (v.option1 === selectedVariant.option1) &&
                (v.option2 === selectedVariant.option2) &&
                (v.option3 === selectedVariant.option3)
            );
            // Fallback to first variant if nothing matches
            variantIdToUse = (found && found.id) ? found.id : (allVariants[0]?.id || selectedVariant.id);
        } else {
            // Fallback
            variantIdToUse = selectedVariant?.id;
        }

        // Always use string-based IDs (legacy compatibility)
        function toIdString(id) {
            if (typeof id === "string" && id.startsWith('gid://')) {
                return id.split('/').pop();
            }
            return String(id);
        }

        // --- BOTTLE BUNDLES: Use addToCartBulk for bundles ---
        if (
            bundleComponents && bundleComponents.length > 0 &&
            product?.type === 'Standard Bundle'
        ) {
            const bundleId = Date.now();
            const bundleItems = [];

            // Add main product with bundle properties
            bundleItems.push({
                id: toIdString(variantIdToUse),
                quantity,
                properties: {
                    _gift_bundle_id: bundleId.toString(),
                    _bundle_final_image_url: modalImage?.replace('https:', '') || '', // Shopify Cart Transforms don't like the https: part
                    _bundle_variant_details: bundleComponents.reduce((acc, comp, idx) => {
                        const selectedVariantId = bundleSelections[idx];
                        const selectedVariant = comp.variants.find(v => String(v.id) === String(selectedVariantId));
                        if (selectedVariant && comp.title && selectedVariant.title !== "Default Title") {
                            acc[comp.title] = selectedVariant.title;
                        }
                        return acc;
                    }, {})
                }
            });

            // Add bundle components with bundle properties
            bundleComponents.forEach((comp, idx) => {
                const selectedVariantId = bundleSelections[idx];
                if (selectedVariantId) {
                    bundleItems.push({
                        id: toIdString(selectedVariantId),
                        quantity,
                        properties: {
                            _gift_bundle_id: bundleId.toString(),
                            // _bundle_item_type: comp.title?.toLowerCase().replace(/\s+/g, '_') || 'component'
                        }
                    });
                }
            });

            if (window.waitForCirkulServices && window.waitForCirkulServices.cart) {
                window.waitForCirkulServices.cart.then(() => {
                    if (window.cirkulServices && window.cirkulServices.cart && typeof window.cirkulServices.cart.addToCartBulk === "function") {
                        window.cirkulServices.cart.addToCartBulk(bundleItems);
                    }
                });
            }
            onClose();
            return;
        }

        // Not a bundle: just add the selected variant - cart.js will add _added_from automatically
        let items = [{ id: toIdString(variantIdToUse), quantity }];

        if (window.waitForCirkulServices && window.waitForCirkulServices.cart) {
            window.waitForCirkulServices.cart.then(() => {
                if (window.cirkulServices && window.cirkulServices.cart && typeof window.cirkulServices.cart.addToCartBulk === "function") {
                    window.cirkulServices.cart.addToCartBulk(items);
                } else if (window.cirkulServices && window.cirkulServices.cart && typeof window.cirkulServices.cart.addToCart === "function") {
                    items.forEach(item => window.cirkulServices.cart.addToCart(item));
                }
            });
        }
        onClose();
    };

    useEffect(() => {
        const handleEsc = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    useEffect(() => {
        // Check URL parameter on mount regardless of openModalById prop
        const variantId = getParameterByName('show');
        if (variantId && !selectedVariant) {
            // Try to find matching variant or product
            let foundVariant = null;
            
            // First check if it matches current product/variant IDs
            if (String(product?.id) === String(variantId) || String(variant?.id) === String(variantId)) {
                // Current modal matches, do nothing since modal should already be open
                return;
            }
            
            // Check variants if available
            if (Array.isArray(allVariants)) {
                foundVariant = allVariants.find(v => 
                    String(v.id) === String(variantId) || 
                    String(v.id).split('/').pop() === String(variantId)
                );
            }
            
            // If we found a matching variant, update selectedVariant
            if (foundVariant) {
                setSelectedVariant(foundVariant);
            }
        }
    }, [product?.id, variant?.id, allVariants, selectedVariant]);


    // Breakpoint Setup 
    const { screenSize } = useScreenSize();
    const isMobile = screenSize === SCREEN_SIZES.MOBILE;
    const isTablet = screenSize === SCREEN_SIZES.TABLET;

    // Only declare these ONCE at the top
    const sipData = product?.sipData || product?.metafields?.cirkul?.sip_modal?.value;
    const flavorLineCollectionGID =
        sipData?.flavor_line_collection || null;

    // Always call the hook, even if handle is null
    const { otherFlavors, otherFlavorsLoading } = useFlavorLineCollection(flavorLineCollectionGID);

    if (!selectedVariant) return null;

    // --- Breadcrumbs logic ---
    // Helper function to generate breadcrumbs based on product type
    const generateBreadcrumbs = () => {
        // Use passed-in sipBreadcrumbs if available
        if (sipBreadcrumbs && Array.isArray(sipBreadcrumbs) && sipBreadcrumbs.length) {
            return sipBreadcrumbs;
        }

        // Check if this is a variety pack product
        const isVarietyPack = actualProduct?.productType === 'Variety Pack' || actualProduct?.productType === 'Starter Kit';
        if (isVarietyPack) {
            return [
                { label: actualProduct.productType, href: "/collections/variety-packs" },
                { label: actualProduct?.title || product?.title }
            ];
        }

        // Check if this is a bottle product
        const isBottle = actualProduct?.productType === 'Bottle';
        if (isBottle) {
            return [
                { label: actualProduct?.productType, href: "/pages/bundle-builder#bottles" },
                { label: actualProduct?.title || product?.title }
            ];
        }

        // Check if this is an accessory product
        const isAccessory = actualProduct?.productType === 'Accessory' || actualProduct?.productType === 'Sleeve' || actualProduct?.productType === 'Lid';
        if (isAccessory) {
            return [
                { label: "Accessories", href: "/pages/bundle-builder#extras" },
                { label: actualProduct?.title || product?.title }
            ];
        }

        // Default breadcrumbs for flavor products
        return [
            { label: "Flavors", href: "#flavors" },
            {
                label: product?.flavorLine?.title || "",
                href: product?.flavorLine?.handle ? `#${product.flavorLine.handle}` : null
            },
            { label: product?.sipData?.title || product?.title }
        ];
    };

    const breadcrumbs = generateBreadcrumbs();

    // --- Gallery images ---
    const galleryImages = Array.isArray(modalGalleryImages) && modalGalleryImages.length
        ? modalGalleryImages
        : (product?.sipData?.modailImages || []);

    // --- Defensive flavorProfiles extraction ---
    // let flavorProfiles = [];
    // if (
    //     product &&
    //     product.sipData &&
    //     Array.isArray(product.sipData.flavorProfiles)
    // ) {
    //     flavorProfiles = product.sipData.flavorProfiles;
    // } else if (
    //     Array.isArray(flavorProfileIcons) &&
    //     Array.isArray(flavorProfileStrings) &&
    //     flavorProfileIcons.length === flavorProfileStrings.length
    // ) {
    //     flavorProfiles = flavorProfileIcons.map((icon, idx) => ({
    //         image: icon.image || icon,
    //         text: flavorProfileStrings[idx] || ""
    //     }));
    // }

    // --- Defensive nutrition image check ---
    // const hasNutritionImage =
    //     actualProduct &&
    //     actualProduct.sipData &&
    //     typeof actualProduct.sipData === "object" &&
    //     actualProduct.sipData.nutrition_image;

    // --- Scroll to top of modal on flavor change (mobile only, for flavor sips) ---
    // Add a data attribute to the modal root for reliable selection
    // In the JSX below, add: data-modal-root
    // function scrollModalToTop() {
    //     if (typeof window === "undefined") return;
    //     if (window.innerWidth > 767) return;
    //     setTimeout(() => {
    //         const modalRoot = document.querySelector('[data-modal-root]');
    //         if (modalRoot) {
    //             const modalContent = modalRoot.querySelector('.tw-relative');
    //             if (modalContent && typeof modalContent.scrollTo === 'function') {
    //                 modalContent.scrollTo({ top: 0, behavior: 'smooth' });
    //             }
    //         }
    //     }, 0);
    // }

    // Modal image logic: prioritize the selected bundle component's image if set,
    // then selected variant, then product, etc.
    function isValidImage(img) {
        if (!img) return false;
        if (
            typeof img === "string" &&
            (
                img.includes('no-image') ||
                img.includes('no-image-2048') ||
                img.includes('no-image-1600') ||
                img.includes('no-image-1024') ||
                img.includes('no-image-512') ||
                img.includes('no-image-256') ||
                img.includes('no-image-128') ||
                img.trim() === ""
            )
        ) {
            return false;
        }
        return true;
    }

    let variantImage = null;
    // If there are multiple variants, and selectedVariant is not set or is the main product, use the first variant's image
    if (Array.isArray(product?.variants) && product.variants.length > 1) {
        let variantToUse = selectedVariant;
        // If selectedVariant is not a real variant (e.g. main product), use first variant
        if (!selectedVariant?.id || !product.variants.some(v => v.id === selectedVariant.id)) {
            variantToUse = product.variants[0];
        }
        if (variantToUse && isValidImage(variantToUse.featuredImage)) {
            variantImage = variantToUse.featuredImage;
        } else if (variantToUse && isValidImage(variantToUse.featured_image)) {
            variantImage = variantToUse.featured_image;
        }
    } else if (Array.isArray(product?.variants) && selectedVariant?.id) {
        // Single variant or selected variant is valid
        const found = product.variants.find(v => v.id === selectedVariant.id);
        if (found && isValidImage(found.featuredImage)) {
            variantImage = found.featuredImage;
        } else if (found && isValidImage(found.featured_image)) {
            variantImage = found.featured_image;
        }

        if (!variantImage) {
            variantImage = isValidImage(variant.featuredImage) && variant.featuredImage;
        }
    }

    let modalImage = encodeURI(
        (isValidImage(activeBundleImage) && activeBundleImage) ||
        (isValidImage(variantImage) && variantImage) ||
        (isValidImage(selectedVariant.featuredImage) && selectedVariant.featuredImage) ||
        (isValidImage(selectedVariant.featured_image) && selectedVariant.featured_image) ||
        (isValidImage(selectedVariant.image) && selectedVariant.image) ||
        (isValidImage(selectedVariant.image_url) && selectedVariant.image_url) ||
        (isValidImage(product?.featuredImage) && product.featuredImage) ||
        (isValidImage(product?.featured_image) && product.featured_image) ||
        (isValidImage(product?.bundle_builder_image) && product.bundle_builder_image) ||
        (Array.isArray(product?.images) && product.images.length > 0 && isValidImage(product.images[0]) ? product.images[0] : "") ||
        (isValidImage(product?.img) && product.img) ||
        (isValidImage(product?.logo) && product.logo) ||
        ""
    );

    // Helper to ensure spinner is hidden if image is already cached
    function handleImageRef(imgRef) {
        if (imgRef && imgRef.complete) {
            setImgLoaded(true);
        }
    }

    // --- Customizer redirect for Customizable Goods ---
    function handleCustomize(e) {
        if (e && typeof e.preventDefault === "function") e.preventDefault();
        if (product?.type !== "Customizable Goods") return;
        const variantId = selectedVariant?.id || product?.id;
        const productId = product?.id;
        const locale = "en-US";
        // Generate visitor code (legacy logic)
        function getVisitorCode() {
            const id = Math.random().toString(36).substring(7);
            try {
                if (window.localStorage) {
                    return (window.localStorage["zakekeGuest"] = window.localStorage["zakekeGuest"] || id);
                }
            } catch (e) {}
            return id;
        }
        const v = getVisitorCode();

        // Create a temporary form and submit as POST to /apps/zakeke?pid=productId
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = (window.zakekeUrl || '/apps/zakeke') + '?pid=' + productId;

        // Add hidden fields
        const zakekeVariantId = document.createElement('input');
        zakekeVariantId.name = 'id';
        zakekeVariantId.type = 'hidden';
        zakekeVariantId.value = variantId;
        form.appendChild(zakekeVariantId);

        const zakekeProductId = document.createElement('input');
        zakekeProductId.name = 'zakeke-product-id';
        zakekeProductId.type = 'hidden';
        zakekeProductId.value = productId;
        form.appendChild(zakekeProductId);

        const zakekeLocale = document.createElement('input');
        zakekeLocale.name = 'zakeke-locale';
        zakekeLocale.type = 'hidden';
        zakekeLocale.value = locale;
        form.appendChild(zakekeLocale);

        const zakekeVisitorId = document.createElement('input');
        zakekeVisitorId.name = 'v';
        zakekeVisitorId.type = 'hidden';
        zakekeVisitorId.value = v;
        form.appendChild(zakekeVisitorId);

        document.body.appendChild(form);
        form.submit();
    }
    // --- Render ---
    return (
       <div
            className="tw-fixed tw-inset-0 tw-z-[1002] tw-flex tw-items-center tw-justify-center tw-bg-[rgba(64,64,66,0.45)] desktop:tw-top-0"
            onClick={onClose}
            data-modal-root
        >
            <div
                className="tw-relative tw-z-[1000] tw-mx-12 tw-flex tw-max-h-[95dvh] tw-min-h-[600px] tw-w-[22.375rem] tw-max-w-full tw-flex-col tw-overflow-y-auto tw-rounded-[5px] tw-border-0 tw-bg-white tw-scrollbar-none desktop:tw-mx-0 desktop:tw-w-[53.125rem] desktop:tw-flex-row"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="tw-absolute tw-right-8 tw-top-8 tw-z-[4] tw-flex tw-size-24 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-4 tw-bg-[#d9d9d9] tw-text-black"
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    <XMarkIcon className='tw-size-20' />
                </button>

                {/* --- Image Carousel --- */}
                {galleryImages?.length > 0 ? (
                    <div 
                        // Only apply background if valid
                        // removing to standardize modals per UAT
                        // TODO, if this passes, we should probably remove the flavorLineBackground-- variables altogether.
                        // style={
                        //     isValidImage(isMobile || isTablet ? product.flavorLineBackgroundMobile : product.flavorLineBackgroundDesktop)
                        //         ? { background: `url(${isMobile || isTablet ? product.flavorLineBackgroundMobile : product.flavorLineBackgroundDesktop})` }
                        //         : undefined
                        // }
                        className="tw-relative tw-w-full tw-items-center tw-justify-center !tw-bg-[length:100%] !tw-bg-[position:center_top] !tw-bg-no-repeat tw-text-center"
                    >
                        <div className="tw-relative tw-flex tw-size-full tw-flex-col-reverse tw-items-center tw-justify-center tw-pt-20">
                            <div className="tw-hidden tw-flex-row tw-justify-center tw-gap-8 desktop:tw-flex desktop:tw-pb-16">
                            {galleryImages?.map((galleryImage,index) => {
                                return(
                                    <button
                                    type="button"
                                    key={index}
                                    className={`tw-relative tw-flex tw-h-[4.375rem] tw-basis-[4.375rem] tw-flex-col tw-items-center tw-justify-center tw-rounded-4 tw-border tw-bg-[#f3f3f7] tw-px-[19px] tw-pb-[7px] tw-pt-[9px] tw-shadow-sm  ${
                                index === localActiveGalleryIdx 
                                    ? "tw-border-[#00aaf5] tw-ring-2 tw-ring-[#00aaf5]" 
                                    : "tw-border-gray-200"
                            }`}
                            onClick={() => setLocalActiveGalleryIdx(index)}
                                >
                                    {galleryImage.image && (
                                    <img
                                        src={galleryImage.image.url ? galleryImage.image.url : galleryImage.image}
                                        alt={product.title}
                                        className="tw-size-full tw-object-contain"
                                        loading="lazy"
                                        ref={handleImageRef}
                                    />
                                    )}
                                </button>
                                )
                            })}
                            </div>
                            <img
                                src={galleryImages[localActiveGalleryIdx].image.url ? galleryImages[localActiveGalleryIdx].image.url : galleryImages[localActiveGalleryIdx].image }
                                alt={selectedVariant.title || product?.title || "Product image"}
                                className="tw-h-auto tw-w-[77.6%] tw-max-w-xs tw-object-contain desktop:tw-w-full"
                                style={imgLoaded ? {} : { visibility: "hidden" }}
                                onLoad={() => setImgLoaded(true)}
                                ref={handleImageRef}
                            />
                            {/* Carousel controls */}
                            {galleryImages?.length > 1 && (isMobile || isTablet) && (
                                <>
                                    <button
                                        className="tw-absolute tw-left-16 tw-top-[38%] tw-size-32 tw-p-2 tw-text-[#66C6FC]"
                                        onClick={() => setLocalActiveGalleryIdx(i => (i - 1 + galleryImages.length) % galleryImages.length)}
                                        aria-label="Previous image"
                                        type="button"
                                    >
                                    <ChevronLeftIcon className="tw-size-24" />
                                    </button>
                                    <button
                                        className="tw-absolute tw-right-16 tw-top-[38%] tw-size-32 tw-p-2 tw-text-[#66C6FC]"
                                        onClick={() => setLocalActiveGalleryIdx(i => (i + 1) % galleryImages.length)}
                                        aria-label="Next image"
                                        type="button"
                                    >
                                    <ChevronRightIcon className="tw-size-24" />
                                    </button>
                                </>
                            )}
                            {/* Pagination dots */}
                            {galleryImages?.length > 1 && (isMobile || isTablet) && (
                                <div className="tw-absolute tw-bottom-2 tw-left-1/2 tw-flex tw--translate-x-1/2 tw-gap-8">
                                    {galleryImages.map((_, idx) => (
                                        <span
                                            key={idx}
                                            className={`tw-inline-block tw-size-10 tw-rounded-full ${idx === localActiveGalleryIdx ? "tw-bg-[#00a3e0]" : "tw-bg-[#ccebff]"}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    // fallback to old modal image logic if no gallery
                    <div   
                    style={{
                            ...(isMobile && product.flavorLineBackgroundMobile
                            ? { background: `url(${product.flavorLineBackgroundMobile})` }
                            : !isMobile && product.flavorLineBackgroundDesktop
                            ? { background: `url(${product.flavorLineBackgroundDesktop})` }
                            : {}),
                        }}
                        className="tw-relative tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-center tw-bg-no-repeat tw-text-center">
                        {!imgLoaded && (
                            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                                <div className="tw-size-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-[#00aaf5] tw-border-t-transparent"></div>
                            </div>
                        )}
                        {modalImage && (
                            <img
                                src={modalImage}
                                alt={selectedVariant.title || product?.title || "Product image"}
                                className="tw-h-full tw-object-contain"
                                style={imgLoaded ? {} : { visibility: "hidden" }}
                                onLoad={() => setImgLoaded(true)}
                                ref={handleImageRef}
                            />
                        )}
                    </div>
                )}

                {/* --- Breadcrumbs --- */}
                <div className="tw-flex tw-w-full tw-flex-col tw-gap-16 tw-px-20 tw-py-16 desktop:tw-px-16 ">
                    <div className="max-tablet:tw-order-1">
                    {breadcrumbs && breadcrumbs.length > 0 && (
                        <div className="sip-breadcrumbs tw-mb-4 tw-text-12 tw-text-gray-500" data-sip-breadcrumbs="">
                            {breadcrumbs.map((b, i) =>
                                b.href && b.href?.length ? (
                                    <span key={i}>
                                        <a href={b.href} data-smooth-scroll dangerouslySetInnerHTML={{ __html: b.label }} />
                                        {i < breadcrumbs.length - 1 && " / "}
                                    </span>
                                ) : (
                                    <span key={i} className="tw-font-semibold" dangerouslySetInnerHTML={{ __html: b.label }} />
                                )
                            )}
                        </div>
                    )}
                    {/* --- Title --- */}
                    <h3
                    className="tw-w-full tw-text-28 tw-font-bold tw-tracking-[-.56px] tw-text-[#3f4443]"
                    dangerouslySetInnerHTML={{
                        __html: `${product?.title || ''}${
                        product?.sipData?.subtitle && !product.handle.includes('puressence') ? ` <span class="tw-text-[#6c7271]">(${product.sipData.subtitle})</span>` : ''
                        }`,
                    }}
                    />

                    {/* --- Product Price --- */}
                    <p className="tw-mb-0 tw-text-28 tw-leading-[40px] tw-tracking-[-.035rem]">{selectedVariant.price || product?.price}</p>
                    </div>

                    {/* --- Flavor Profiles --- */}
                    {product?.flavorProfiles && product?.flavorProfiles.length > 0 &&
                        <div className="tw-flex tw-flex-wrap tw-gap-y-[.59375rem] max-tablet:tw-order-3">
                            {product.flavorProfiles.map((profile, index) => (
                                <div
                                    key={index}
                                    className="tw-relative tw-flex tw-items-center tw-gap-4 after:tw-mx-8 after:tw-h-24 after:tw-border-l after:tw-border-black after:tw-content-[''] last:after:tw-content-none"
                                >
                                    <img src={profile.image} alt={profile.text} />
                                    <p className="tw-whitespace-nowrap tw-text-[.75rem] tw-leading-[.875rem] tw-text-[#8e9593]">
                                        {profile.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    }

                    <div className="tw-mb-8 tw-text-14 max-tablet:tw-order-4">
                        <div
                            className="[&>*:first-child]:tw-text-16 [&>*]:tw-text-[#3f4443] [&>ul]:tw-ml-20 [&>ul]:tw-list-disc [&>ul]:tw-text-[#aaa] [&>ul]:tw-list-outside [&>p:first-child]:tw-mb-24 [&_img]:tw-inline-block"
                            dangerouslySetInnerHTML={{ __html: product?.description }}
                        />
                    </div>

                    {product.nutritionImage && !product.nutritionImage.includes('no-image') &&
                        <div className="max-tablet:tw-order-5"> 
                            <button
                                className="tw-text-16 tw-font-bold tw-leading-18 tw-text-[#00a3e0]"
                                onClick={() => setNutritionVisible(true)} 
                            >
                                See Nutrition Facts &amp; Ingredients
                            </button>
                            {nutritionVisible && 
                            <div
                                onClick={() => setNutritionVisible(false)} 
                                className="tw-absolute tw-left-0 tw-top-0 tw-z-[3] tw-flex tw-size-full tw-cursor-pointer tw-items-center tw-justify-center tw-bg-[rgba(64,64,66,.25)]"
                            >
                                <img className='tw-h-auto tw-w-full tablet:tw-h-full tablet:tw-w-auto' src={product.nutritionImage} alt={`${product.title} Nutrition Label`} />
                            </div>
                                }
                        </div>
                    }

                    {/* Starter Kit Nutrition */}
                    {product.starterKitNutrition && product.starterKitNutrition.length > 0 &&
                        <div className="max-tablet:tw-order-5"> 
                            <button
                                className="tw-text-16 tw-font-bold tw-leading-18 tw-text-[#00a3e0]"
                                onClick={() => {
                                    setNutritionVisible(true);
                                    requestAnimationFrame(() => {
                                      nutritionRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
                                    });
                                  }}
                                       
                            >
                                See Nutrition Facts &amp; Ingredients
                            </button>
                            {nutritionVisible && 
                                <div
                                    ref={nutritionRef}
                                    onClick={() => setNutritionVisible(false)} 
                                    className="tw-absolute tw-left-0 tw-top-0 tw-z-[3] tw-size-full tw-cursor-pointer tw-items-center tw-justify-center tw-bg-[rgba(64,64,66,.25)]"
                                >
                                    <div className="tw-overflow-auto tw-flex-col desktop:tw-flex-row tw-gap-8 tw-flex tw-justify-center desktop:tw-h-full">
                                        {product.starterKitNutrition.map((image, index) => (
                                            <img key={index}
                                            className='tw-h-auto tw-w-full tablet:tw-h-full tablet:tw-w-auto tw-max-h-[80vh] tw-object-contain' src={image} alt={`${product.title} Nutritional Labels`} />
                                        ))}
                                    </div>
                                </div>
                            }
                        </div>
                    }

                    {/* Variety Pack message - show above quantity/add to cart buttons */}
                    {actualProduct?.productType === 'Variety Pack' && (
                        <div className="tw-text-12 tw-bg-[#E5F5FF] tw-text-[#0072A6] tw-px-10 tw-py-10 tw-rounded-4">
                            Add 4 Variety Packs to your cart—your 4th is free at checkout.
                        </div>
                    )}

                    {/* --- BUTTON/FORM LOGIC --- */}
                    {actualProduct?.type === "Customizable Goods" ? (
                        // Only for Customizable Goods: show Customize button or form, never Add to Cart
                        hasVariants && optionGroups.length > 0 ? (
                            <section className="max-tablet:tw-order-2">
                                <form className="product-add-to-cart-form" onSubmit={e => { e.preventDefault(); handleCustomize(e); }}>
                                    <div className="tw-mb-8 tw-flex tw-flex-row tw-items-end tw-gap-16">
                                        <label className="tw-shrink-0">
                                            <ProductQty
                                                qtyCallback={setQuantity}
                                                onChange={handleQuantityChange}
                                                className="!tw-h-[44px] !tw-w-full !tw-gap-[.5rem]"
                                                min={1}
                                            />
                                        </label>
                                        {optionGroups.map((group, idx) => {
                                            let filteredValues = group.values.filter(val =>
                                                allVariants.some(v =>
                                                    (!v.launch_timestamp || v.launch_timestamp <= nowTimestamp) &&
                                                    v.options &&
                                                    v.options[idx] === val
                                                )
                                            );
                                            if (filteredValues.length > 1) {
                                                const currentValue = getCurrentOptionValues()[idx] || filteredValues[0];
                                                return (
                                                    <label
                                                        key={group.name}
                                                        className="tw-flex tw-w-2/3 tw-flex-1 tw-flex-col tw-gap-2"
                                                    >
                                                        <span className="tw-mb-2 tw-font-semibold tw-text-[#3F4443]">{group.name}</span>
                                                        <select
                                                            name={group.name}
                                                            value={currentValue}
                                                            onChange={e => handleOptionChange(idx, e.target.value)}
                                                            className="tw-rounded-[.25rem]  tw-border tw-border-[#00aaf5] tw-bg-white tw-py-2 tw-pl-8 tw-pr-24 tw-text-[#3F4443] tw-outline-none focus:tw-border-[#0077b6]"
                                                            style={{ minWidth: 100 }}
                                                        >
                                                            {filteredValues.map(val => (
                                                                <option key={val} value={val}>{val}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                    {isCurrentSelectionOutOfStock ? (
                                        <button
                                            type="button"
                                            className="tw-flex tw-justify-center tw-items-center tw-mt-8 tw-h-[3.125rem] tw-w-full tw-rounded-4 tw-leading-16 tw-bg-gray-400 tw-p-16 tw-font-bold tw-text-white"
                                            disabled
                                        >
                                            Unavailable
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="tw-flex tw-justify-center tw-items-center tw-mt-8 tw-h-[3.125rem] tw-w-full tw-rounded-4 tw-leading-16 tw-bg-[#00aaf5] tw-p-16 tw-font-bold tw-text-white hover:tw-bg-[#0077b6]"
                                        >
                                            Customize
                                            <span className="add-button-separator">&nbsp;|&nbsp;</span>
                                            <span data-variant-price-label="">$
                                        {
                                            (Number(
                                                (selectedVariant.price || product?.price)?.toString().replace(/[^0-9.]/g, '')
                                            ) * Number(quantity)
                                        ).toFixed(2)
                                    }
                                    </span>
                                        </button>
                                    )}
                                </form>
                            </section>
                        ) : (
                            isCurrentSelectionOutOfStock ? (
                                <button
                                    type="button"
                                    className="tw-flex tw-justify-center tw-items-center tw-mt-8 tw-h-[3.125rem] tw-w-full tw-rounded-4 tw-leading-16 tw-bg-gray-400 tw-p-16 tw-font-bold tw-text-white"
                                    disabled
                                >
                                    Unavailable
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="tw-flex tw-justify-center tw-items-center tw-mt-8 tw-h-[3.125rem] tw-w-full tw-rounded-4 tw-leading-16 tw-bg-[#00aaf5] tw-p-16 tw-font-bold tw-text-white hover:tw-bg-[#0077b6]"
                                    onClick={handleCustomize}
                                >
                                    Customize
                                    <span className="add-button-separator">&nbsp;|&nbsp;</span>
                                    <span data-variant-price-label="">$
                                {
                                    (Number(
                                        (selectedVariant.price || product?.price)?.toString().replace(/[^0-9.]/g, '')
                                    ) * Number(quantity)
                                ).toFixed(2)
                            }
                            </span>
                                </button>
                            )
                        )
                    ) : (
                        // Add to Cart and bundle logic for all other product types...
                        <>
                            {hasVariants && optionGroups.length > 0 && (
                                <section className="max-tablet:tw-order-3">
                                    <form className="product-add-to-cart-form" onSubmit={handleAdd}>
                                        <div className="tw-mb-8 tw-flex tw-flex-row tw-items-end tw-gap-16">
                                            <label className="tw-shrink-0">
                                                <ProductQty
                                                    qtyCallback={setQuantity}
                                                    onChange={handleQuantityChange}
                                                    className="!tw-h-[44px] !tw-w-full !tw-gap-[.5rem]"
                                                    min={1}
                                                />
                                            </label>
                                            {optionGroups.map((group, idx) => {
                                                let filteredValues = group.values.filter(val =>
                                                    allVariants.some(v =>
                                                        (!v.launch_timestamp || v.launch_timestamp <= nowTimestamp) &&
                                                        v.options &&
                                                        v.options[idx] === val
                                                    )
                                                );
                                                if (filteredValues.length > 1) {
                                                    const currentValue = getCurrentOptionValues()[idx] || filteredValues[0];
                                                    return (
                                                        <label
                                                            key={group.name}
                                                            className="tw-flex tw-w-2/3 tw-flex-1 tw-flex-col tw-gap-2"
                                                        >
                                                            <span className="tw-mb-2 tw-font-semibold tw-text-[#3F4443]">{group.name}</span>
                                                            <select
                                                                name={group.name}
                                                                value={currentValue}
                                                                onChange={e => handleOptionChange(idx, e.target.value)}
                                                                className="tw-rounded-[.25rem]  tw-border tw-border-[#00aaf5] tw-bg-white tw-py-2 tw-pl-8 tw-pr-24 tw-text-[#3F4443] tw-outline-none focus:tw-border-[#0077b6]"
                                                                style={{ minWidth: 100 }}
                                                            >
                                                                {filteredValues.map(val => (
                                                                    <option key={val} value={val}>{val}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                        {/* Bundle component dropdowns */}
                                        {bundleComponents && bundleComponents.length > 0 && (
                                            <div className="tw-mb-8">
                                                {bundleComponents.map((comp, compIdx) => {
                                                    let validVariants = Array.isArray(comp.variants)
                                                        ? comp.variants.filter(
                                                            v =>
                                                                v &&
                                                                v.title &&
                                                                v.title !== "Default Title" &&
                                                                v.id
                                                        )
                                                        : [];
                                                    if (validVariants.length === 0) {
                                                        return null;
                                                    }
                                                    return (
                                                        <label key={compIdx} className="tw-flex tw-min-w-[120px] tw-flex-1 tw-flex-col tw-gap-2">
                                                            <span className="tw-mb-2 tw-font-semibold tw-text-[#3F4443]">{comp.title || `Bundle Option ${compIdx + 1}`}</span>
                                                            <select
                                                                value={bundleSelections[compIdx] || validVariants[0]?.id || ""}
                                                                onChange={e => handleBundleChange(compIdx, e.target.value)}
                                                                className="tw-rounded-[.25rem]  tw-border tw-border-[#00aaf5] tw-bg-white tw-py-2 tw-pl-8 tw-pr-24 tw-text-[#3F4443] tw-outline-none focus:tw-border-[#0077b6]"
                                                                style={{ minWidth: 100 }}
                                                            >
                                                                {validVariants.map(v => (
                                                                    <option key={v.id} value={v.id}>{v.title}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {isCurrentSelectionOutOfStock ? (
                                            <button
                                                type="button"
                                                className="tw-flex tw-justify-center tw-items-center tw-mt-8 tw-h-[3.125rem] tw-w-full tw-rounded-4 tw-leading-16 tw-bg-gray-400 tw-p-16 tw-font-bold tw-text-white"
                                                disabled
                                            >
                                                Unavailable
                                            </button>
                                        ) : (
                                            <button type="submit" className="tw-flex tw-justify-center tw-items-center tw-mt-8 tw-h-[3.125rem] tw-w-full tw-rounded-4 tw-leading-16 tw-bg-[#00aaf5] tw-p-16 tw-font-bold tw-text-white hover:tw-bg-[#0077b6]">
                                                Add to Cart
                                                <span className="add-button-separator">&nbsp;|&nbsp;</span>
                                                <span data-variant-price-label="">$
                                                    {(
                                                        Number(
                                                            (selectedVariant.price || product?.price)?.toString().replace(/[^0-9.]/g, '')
                                                        ) * Number(quantity)
                                                    ).toFixed(2)
                                                    }
                                                </span>
                                            </button>
                                        )}
                                    </form>
                                </section>
                            )}
                            {(!hasVariants || optionGroups.length === 0) && (
                                <form
                                    className="tw-flex tw-flex-col tw-gap-8 max-tablet:tw-order-3"
                                    onSubmit={handleAdd}
                                >
                                    {/* --- First row: Bundle dropdowns --- */}
                                    {bundleComponents && bundleComponents.length > 0 && (
                                        <div className="tw-mb-8 tw-flex tw-flex-row tw-gap-8">
                                            {bundleComponents.map((comp, compIdx) => {
                                                let validVariants = Array.isArray(comp.variants)
                                                    ? comp.variants.filter(
                                                        v =>
                                                            v &&
                                                            v.title &&
                                                            v.title !== "Default Title" &&
                                                            v.id
                                                    )
                                                    : [];
                                                if (validVariants.length === 0) {
                                                    return null;
                                                }
                                                return (
                                                    <label
                                                        key={compIdx}
                                                        className="tw-min-w-0 tw-flex tw-flex-1 tw-flex-col tw-gap-2"
                                                        style={{ minWidth: 100 }}
                                                    >
                                                        <span className="tw-mb-2 tw-font-semibold tw-text-[#3F4443]">
                                                            {comp.title || `Bundle Option ${compIdx + 1}`}
                                                        </span>
                                                        <select
                                                            value={bundleSelections[compIdx] || validVariants[0]?.id || ""}
                                                            onChange={e => handleBundleChange(compIdx, e.target.value)}
                                                            className="tw-w-full tw-rounded-[.25rem] tw-border tw-border-[#00aaf5] tw-bg-white tw-py-2 tw-pl-8 tw-pr-24 tw-text-[#3F4443] tw-outline-none focus:tw-border-[#0077b6]"
                                                        >
                                                            {validVariants.map(v => (
                                                                <option key={v.id} value={v.id}>{v.title}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {/* --- Second row: Quantity and Add to Cart --- */}
                                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-16">
                                        <div className="tw-shrink-0">
                                            <ProductQty
                                                qtyCallback={setQuantity}
                                                onChange={handleQuantityChange}
                                                className="!tw-h-[44px] !tw-w-[115px] !tw-gap-[.5rem]"
                                                min={1}
                                            />
                                        </div>
                                        {isCurrentSelectionOutOfStock ? (
                                            <button
                                                type="button"
                                                className="tw-flex-1 tw-whitespace-nowrap tw-rounded-4 tw-leading-16 tw-bg-gray-400 tw-p-[17px] tablet:tw-p-[17px] tw-font-bold tw-text-white"
                                                style={{ minWidth: 0 }}
                                                disabled
                                            >
                                                Unavailable
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                className="tw-flex-1 tw-whitespace-nowrap tw-rounded-4 tw-leading-16 tw-bg-[#00aaf5] tw-p-[17px] tablet:tw-p-[17px] tw-font-bold tw-text-white hover:tw-bg-[#0077b6]"
                                                style={{ minWidth: 0 }}
                                            >
                                                Add to Cart
                                                <span className="add-button-separator">&nbsp;|&nbsp;</span>
                                                <span data-variant-price-label="">$
                                                    {
                                                        (Number(
                                                            (selectedVariant.price || product?.price)?.toString().replace(/[^0-9.]/g, '')
                                                        ) * Number(quantity)).toFixed(2)
                                                    }
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </form>
                            )}
                        </>
                    )}

                {/* --- OTHER FLAVORS SECTION --- */}
                {flavorLineCollectionGID && (
                    <div className="tw-mt-auto max-tablet:tw-order-6">
                        <h5 className="tw-mb-4 tw-text-12 tw-text-[#8e9593] ">Other Flavors:</h5>
                        <div className="tw-overflow-auto tw-scrollbar-none desktop:tw-max-h-[166px]">
                            <div className="tw-flex tw-flex-wrap tw-gap-8">
                                {otherFlavorsLoading && otherFlavors.length === 0 && (
                                    <div className="tw-flex tw-h-32 tw-w-full tw-items-center tw-justify-center">
                                        <div className="tw-size-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-[#00aaf5] tw-border-t-transparent"></div>
                                    </div>
                                )}
                                {otherFlavors
                                    .filter(sip => {
                                        const EXCLUDED_COLLECTION = "variety-packs";
                                        return !sip.collections?.edges?.some(
                                        ({ node }) => node.handle === EXCLUDED_COLLECTION
                                        );
                                    })
                                    .map((sip) => {
                                        const firstImageUrl = sip.modalContent?.reference?.firstImage?.firstRef?.nodes[0]?.image?.url;
                                        return (
                                        <button
                                            key={sip.id}
                                            type="button"
                                            className={`tw-relative tw-flex tw-h-[4.375rem] tw-basis-[4.375rem] tw-flex-col tw-items-center tw-justify-center tw-rounded-4 tw-border-2 tw-border-solid tw-bg-[#f3f3f7] tw-px-[19px] tw-pb-[7px] tw-pt-[9px] tw-shadow-sm ${
                                            normalizeShopifyId(sip.id) === normalizeShopifyId(product.id)
                                                ? "tw-border-[#00aaf5]" 
                                                : "tw-border-transparent"
                                            }`}
                                            aria-label={sip.title}
                                            tabIndex={0}
                                            onClick={() => {
                                                // Only scroll on mobile, and only if flavor is changing
                                                if (typeof window !== "undefined" && window.innerWidth <= 767 && normalizeShopifyId(sip.id) !== normalizeShopifyId(product.id)) {}
                                                
                                                // Safely extract flavor profiles with proper fallbacks
                                                let flavorProfiles = [];
                                                try {
                                                    const profileStrings = sip.flavorProfiles?.reference?.flavor_profile_strings?.value;
                                                    const profileIcons = sip.flavorProfiles?.reference?.flavor_profile_icons?.references?.nodes;
                                                    
                                                    if (Array.isArray(profileStrings) && Array.isArray(profileIcons)) {
                                                        flavorProfiles = profileStrings.map((profileString, idx) => ({
                                                            image: profileIcons[idx]?.icon?.reference?.image?.url || '',
                                                            text: profileString
                                                        }));
                                                    } else {
                                                        // Fallback to existing product flavor profiles
                                                        flavorProfiles = product.flavorProfiles || [];
                                                    }
                                                } catch (error) {
                                                    console.warn('Error extracting flavor profiles:', error);
                                                    flavorProfiles = product.flavorProfiles || [];
                                                }
                                                
                                                onFlavorSelect({
                                                    ...product,
                                                    id: sip.id,
                                                    title: sip.title,
                                                    handle: sip.handle,
                                                    description: sip.description || "No description available",
                                                    nutritionImage: sip.modalContent?.reference?.nutritionImage?.reference?.image?.url,
                                                    sipData: sip.sipData || product.sipData,
                                                    modalGalleryImages: sip.modalContent?.reference?.firstImage?.firstRef?.nodes,
                                                    flavorProfiles: flavorProfiles,
                                                    variants: sip.variants || product.variants || []
                                                });
                                            }}     
                                        >
                                            {firstImageUrl && (
                                            <img
                                                src={firstImageUrl}
                                                alt={sip.title}
                                                className="tw-size-full tw-object-contain"
                                                loading="lazy"
                                            />
                                            )}
                                        </button>
                                        );
                                    })
                                }
                                {otherFlavorsLoading && otherFlavors.length > 0 && (
                                    <div className="tw-flex tw-h-32 tw-w-full tw-items-center tw-justify-center">
                                        <div className="tw-size-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-[#00aaf5] tw-border-t-transparent"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default ProductModal;

// --- Modal opener hook for parent usage ---
export function useProductModalOpener(products, setSelectedProduct) {
    useEffect(() => {
        const variantId = getParameterByName('show');
        if (variantId && products && products.length) {
            let found = products.find(p =>
                p.id == variantId ||
                (Array.isArray(p.variants) && p.variants.some(v => v.id == variantId))
            );
            if (found) setSelectedProduct(found);
        }
    }, [products, setSelectedProduct]);
}
